import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Grid,
  Typography,
  Button,
  FormControl,
  makeStyles,
  CircularProgress,
  MenuItem,
  Divider,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
} from "@material-ui/core";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteIcon from "@material-ui/icons/Delete";
import { inputFieldStyles, commonInlineStyles } from "../../styles";
import { appUrl } from "../../../../../../../utils/config";
// import { useLocation } from "react-router-dom";
import { useApiBaseUrl } from "../../../CommonMethods/CommonMethods";

const useStyles = makeStyles((theme) => ({
  formTitle: {
    marginBottom: theme.spacing(4),
    fontWeight: 700,
    fontSize: 32,
    position: 'relative',
  },
  formField: {
    width: "100%",
    // marginBottom: theme.spacing(3),
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
  button: {
    minWidth: 180,
    // fontWeight: 600,
    // fontSize: 16,
    borderRadius: 30,
    // height: 44,
  },
  backButton: {
    minWidth: 180,
    borderRadius: 30,
    background: '#707070',
    color: '#fff',
    marginRight: theme.spacing(2),
  },
  tableHeader: {
    fontWeight: 600,
    backgroundColor: '#F0F0F0',
    // whiteSpace: 'nowrap',
  },
  tableCell: {
    verticalAlign: 'middle',
  },
  addSiteButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: 999,
    textTransform: 'none',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000',
    color: '#000',
    padding: theme.spacing(0.5, 1.5),
    minWidth: 'auto',
    fontSize: 12,
    lineHeight: 1.5,
  },
}));

const createEmptyPlant = () => ({
  mfgPlantID: '',
  manufacturingPlantIdSameAsASL: '',
  mfgPlantShortName: '',
});

const createEmptySite = () => ({
  siteID: '',
  siteShortName: '',
  siteHasMultiplePlants: '',
  manufacturingPlantDataList: [createEmptyPlant()],
});

const SiteDetails = (props) => {
  const { updateStepData, handleNext, handleBack, formData, readOnly = false, cmoStepDetails, onFieldChange, hideStepActions = false, selectedCMOFormData, originalVendorPlantDetails } = props;
  // Debug log for incoming props

  // Get the best available data source - prioritize direct API response
  const dataSource = originalVendorPlantDetails?.vendorPlantDetails?.vendorPlantDetails || selectedCMOFormData?.vendorPlantDetails || formData || {};

  const [stepData, setStepData] = useState({
    ...formData,
    cmoHasMultipleSites: typeof formData?.cmoHasMultipleSites === "boolean" ? formData?.cmoHasMultipleSites === true ? "Yes" : "No" : typeof formData?.cmoHasMultipleSites === "string" ? formData?.cmoHasMultipleSites : ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);
  const [siteDetailsChanged, setSiteDetailsChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [brandOwnerMosymphonyID, setBrandOwnerMosymphonyID] = useState("");
  const siteDetailsAddedOnce = sessionStorage.getItem("siteDetailsAddedOnce") === "true";
  const API_BASE_URL = useApiBaseUrl(appUrl);

  const [siteRows, setSiteRows] = useState([createEmptySite()]);
  // const location = useLocation();

  function mergeCMOSites(updatedSites = [], originalSites = []) {
    const safe = (arr) => (Array.isArray(arr) ? arr : []);


    const normalizedOriginal = safe(originalSites).map(site => ({
      siteDetails: site?.siteDetails || {},
      manufacturingPlantDataList: safe(site?.manufacturingPlantDataList).map(
        p => (p?.mfgPlantDetails != null ? { ...p.mfgPlantDetails } : { ...p })
      )
    }));

    const originalBySiteID = new Map();
    const originalByCMOSiteID = new Map();
    normalizedOriginal.forEach(site => {
      const id = site?.siteDetails?.siteID;
      const cmoId = site?.siteDetails?.collabrixCMOSiteID;
      if (id != null) originalBySiteID.set(id, site);
      if (cmoId != null) originalByCMOSiteID.set(cmoId, site);
    });

    const result = [];

    const flattenPlant = (p) => (p?.mfgPlantDetails != null ? { ...p.mfgPlantDetails } : { ...p });

    safe(updatedSites).forEach(updatedSite => {
      const siteDetails = updatedSite?.siteDetails || {};
      const siteID = siteDetails.siteID;
      const collabrixCMOSiteID = siteDetails.collabrixCMOSiteID;
      const originalSite =
        originalBySiteID.get(siteID) ?? (collabrixCMOSiteID != null ? originalByCMOSiteID.get(collabrixCMOSiteID) : undefined);

      const mergedPlants = [];
      const updatedPlantIds = new Set(
        safe(updatedSite.manufacturingPlantDataList)
          .map(p => flattenPlant(p)?.collabrixMfgPlantID)
          .filter(id => id != null)
      );
      const originalPlants = safe(originalSite?.manufacturingPlantDataList).filter(
        p => (p?.mfgPlantDetails != null ? p.mfgPlantDetails : p)?.collabrixMfgPlantID != null
      );

      safe(updatedSite.manufacturingPlantDataList).forEach(plant => {
        const flatPlant = flattenPlant(plant);
        const mfgPlantId = flatPlant?.collabrixMfgPlantID;
        mergedPlants.push({
          collabrixCMOSiteID: flatPlant?.collabrixCMOSiteID ?? collabrixCMOSiteID,
          collabrixMfgPlantID: mfgPlantId,
          mfgPlantID: flatPlant?.mfgPlantID,
          mfgPlantShortName: flatPlant?.mfgPlantShortName,
          aslNumber: flatPlant?.aslNumber,
          markForDelete: mfgPlantId != null ? false : false
        });
      });

      originalPlants.forEach(plant => {
        const flat = flattenPlant(plant);
        const id = flat?.collabrixMfgPlantID;
        if (id != null && !updatedPlantIds.has(id)) {
          mergedPlants.push({
            collabrixCMOSiteID: flat?.collabrixCMOSiteID ?? collabrixCMOSiteID,
            collabrixMfgPlantID: flat?.collabrixMfgPlantID,
            mfgPlantID: flat?.mfgPlantID,
            mfgPlantShortName: flat?.mfgPlantShortName,
            aslNumber: flat?.aslNumber,
            markForDelete: id != null
          });
        }
      });

      const origSiteDetails = originalSite?.siteDetails || {};
      const mergedSiteDetails = {
        ...origSiteDetails,
        ...siteDetails,
        markForDelete: false
      };

      result.push({
        siteDetails: mergedSiteDetails,
        manufacturingPlantDataList: mergedPlants
      });

      if (siteID != null) originalBySiteID.delete(siteID);
      if (originalSite?.siteDetails?.collabrixCMOSiteID != null) {
        originalByCMOSiteID.delete(originalSite.siteDetails.collabrixCMOSiteID);
      }
    });

    // ===== sites only in original (deleted in UI) → site and all plants markForDelete: true =====
    const remainingOriginalSites = normalizedOriginal.filter(
      site =>
        !result.some(
          r =>
            r.siteDetails?.siteID === site.siteDetails?.siteID ||
            r.siteDetails?.collabrixCMOSiteID === site.siteDetails?.collabrixCMOSiteID
        )
    );
    remainingOriginalSites.forEach(site => {
      const siteId = site?.siteDetails?.collabrixCMOSiteID;
      result.push({
        siteDetails: {
          ...site.siteDetails,
          markForDelete: siteId != null
        },
        manufacturingPlantDataList: safe(site.manufacturingPlantDataList).map(p => {
          const flat = flattenPlant(p);
          const mfgPlantId = flat?.collabrixMfgPlantID ?? p?.collabrixMfgPlantID;
          return { ...flat, ...p, markForDelete: mfgPlantId != null };
        })
      });
    });

    return result;
  }



  useEffect(() => {
    // Skip updating in readonly mode
    // if (readOnly) return;
    if (readOnly) {
      if (formData?.cmoSites && Array.isArray(formData.cmoSites) && formData.cmoSites.length > 0) {
        const normalizedSites = formData.cmoSites.map((site) => {
          const plants = site?.manufacturingPlantDataList && Array.isArray(site.manufacturingPlantDataList) && site.manufacturingPlantDataList.length > 0
            ? site.manufacturingPlantDataList.map((plant) => ({
              ...plant?.mfgPlantDetails,
              manufacturingPlantIdSameAsASL:
                plant?.mfgPlantDetails?.mfgPlantID && plant?.mfgPlantDetails?.aslNumber && String(plant?.mfgPlantDetails.aslNumber).trim() === String(plant?.mfgPlantDetails.mfgPlantID).trim()
                  ? "Yes"
                  : "No",
            }))
            : [createEmptyPlant()];

          return {
            ...site?.siteDetails,
            siteHasMultiplePlants: plants.length > 1 ? "Yes" : "No",
            manufacturingPlantDataList: plants,
          };
        });
        setSiteRows(normalizedSites);
      }
      return;
    }

    // Use dataSource for country information, fallback to formData
    const sourceData = (Object.keys(dataSource).length > 0) ? dataSource : formData;


    const newStepData = {
      ...sourceData,
      ...formData, // Keep existing formData to not break functionality
      // country: countryCode || sourceData?.country || formData?.country || '',
      // countryName: countryName || sourceData?.country || formData?.countryName || '',
      // state: stateCode || sourceData?.state || formData?.state || '',
      // stateName: stateName || sourceData?.state || formData?.stateName || '',
    };
    if (newStepData?.cmoSites && Array.isArray(newStepData.cmoSites) && newStepData.cmoSites.length > 0 && !siteRows[0].siteID) {
      const normalizedSites = newStepData.cmoSites.map((site) => {
        if (site?.manufacturingPlantDataList) {
          const plants = site?.manufacturingPlantDataList && Array.isArray(site.manufacturingPlantDataList) && site.manufacturingPlantDataList.length > 0
            ? site.manufacturingPlantDataList.map((plant) => ({
              ...plant?.mfgPlantDetails,
              manufacturingPlantIdSameAsASL:
                plant?.mfgPlantDetails?.mfgPlantID && plant?.mfgPlantDetails?.aslNumber && String(plant?.mfgPlantDetails.aslNumber).trim() === String(plant?.mfgPlantDetails.mfgPlantID).trim()
                  ? "Yes"
                  : "No",
            }))
            : [createEmptyPlant()];

          return {
            ...site?.siteDetails,
            siteHasMultiplePlants: plants.length > 1 ? "Yes" : "No",
            manufacturingPlantDataList: plants,
          };
        } else if (site?.siteManufacturingPlants) {
          const plants = site?.siteManufacturingPlants && Array.isArray(site.siteManufacturingPlants) && site.siteManufacturingPlants.length > 0
            ? site.siteManufacturingPlants.map((plant) => ({
              ...plant,
              // manufacturingPlantIdSameAsASL:
              //   plant?.mfgPlantID && plant?.aslNumber && String(plant?.aslNumber).trim() === String(plant?.mfgPlantID).trim()
              //     ? "Yes"
              //     : "No",
            }))
            : [createEmptyPlant()];

          return {
            ...site,
            siteHasMultiplePlants: plants.length > 1 ? "Yes" : "No",
            manufacturingPlantDataList: plants,
          };
        } else {
          return createEmptySite()
        }

      });
      setSiteRows(normalizedSites);
    }

    setStepData({
      ...newStepData, cmoHasMultipleSites: typeof newStepData?.cmoHasMultipleSites === "boolean" ? newStepData?.cmoHasMultipleSites === true ? "Yes" : "No" : typeof newStepData?.cmoHasMultipleSites === "string" ? newStepData?.cmoHasMultipleSites : ''
    });

    // Store original data for change detection if not already set
    if (!originalData) {
      setOriginalData(newStepData);
    }
  }, [formData, readOnly, dataSource]);

  // Effect to detect changes for update scenario
  useEffect(() => {
    if (!originalData || !stepData) return;

    if (originalData?.cmoSites && Array.isArray(originalData.cmoSites) && originalData.cmoSites.length > 0) {
      const originalNormalizedSites = originalData.cmoSites.map((site) => {
        const plants = site?.manufacturingPlantDataList && Array.isArray(site.manufacturingPlantDataList) && site.manufacturingPlantDataList.length > 0
          ? site.manufacturingPlantDataList.map((plant) => ({
            ...plant?.mfgPlantDetails,
            manufacturingPlantIdSameAsASL:
              plant?.mfgPlantDetails?.mfgPlantID && plant?.mfgPlantDetails?.aslNumber && String(plant?.mfgPlantDetails.aslNumber).trim() === String(plant?.mfgPlantDetails.mfgPlantID).trim()
                ? "Yes"
                : "No",
          }))
          : [createEmptyPlant()];

        return {
          ...site?.siteDetails,
          siteHasMultiplePlants: plants.length > 1 ? "Yes" : "No",
          manufacturingPlantDataList: plants,
        };
      });

      const sitesMatch = (a, b) => {
        if (!a || !b) return false;
        const siteIdMatch = String(a.siteID || "").trim() === String(b.siteID || "").trim();
        const siteNameMatch = String(a.siteShortName || "").trim() === String(b.siteShortName || "").trim();
        const plantsA = a.manufacturingPlantDataList || [];
        const plantsB = b.manufacturingPlantDataList || [];
        if (plantsA.length !== plantsB.length) return false;
        const plantsMatch = plantsA.every((pa, i) => {
          const pb = plantsB[i] || {};
          return (
            String(pa?.mfgPlantID || "").trim() === String(pb?.mfgPlantID || "").trim() &&
            String(pa?.mfgPlantShortName || "").trim() === String(pb?.mfgPlantShortName || "").trim() &&
            String(pa?.aslNumber ?? "").trim() === String(pb?.aslNumber ?? "").trim()
          );
        });
        return siteIdMatch && siteNameMatch && plantsMatch;
      };

      const hasSiteRowsChanged =
        siteRows.length !== originalNormalizedSites.length ||
        siteRows.some((row, i) => !sitesMatch(row, originalNormalizedSites[i]));

      const originalCmoHasMultipleSites =
        typeof originalData?.cmoHasMultipleSites === "boolean"
          ? originalData.cmoHasMultipleSites === true
            ? "Yes"
            : "No"
          : "";
      const cmoHasMultipleSitesChanged =
        originalCmoHasMultipleSites !== "" &&
        stepData.cmoHasMultipleSites !== "" &&
        originalCmoHasMultipleSites !== stepData.cmoHasMultipleSites;

      const siteFieldsChanged = cmoHasMultipleSitesChanged || hasSiteRowsChanged;
      setSiteDetailsChanged(siteFieldsChanged);
    } else {
      setSiteDetailsChanged(false);
    }
  }, [stepData, originalData, siteRows]);



  // When "CMO has multiple sites" is "No", ensure only one site row
  useEffect(() => {
    if (stepData.cmoHasMultipleSites === "No" && siteRows.length > 1) {
      setSiteRows((prev) => [prev[0] || createEmptySite()]);
    }
  }, [stepData.cmoHasMultipleSites, siteRows.length]);

  // Auto-populate site short name from CMO short name when CMO has multiple sites = No
  // Only populate if site short name is not already set
  useEffect(() => {
    if (!readOnly && stepData.cmoHasMultipleSites === "No") {
      const cmoShortName = cmoStepDetails?.organizationDetails?.orgShortName || "";
      console.log(cmoShortName)
      setSiteRows(prev => {
        if (prev[0]?.siteShortName) return prev;
        const next = [...prev];
        if (next[0]) {
          next[0] = { ...next[0], siteShortName: cmoShortName };
        }
        return next;
      });
    }
  }, [stepData.cmoHasMultipleSites, cmoStepDetails, readOnly]);

  const classes = useStyles();

  const handleSiteFieldChange = (siteIndex, field, value) => {
    setErrors((prev) => {
      let variable = `site_${siteIndex}_${field}`
      const { [variable]: Removed, ...rest } = prev;
      return rest;
    })
    if (field === "siteHasMultiplePlants" && value === "No") {
      setSiteRows(prev => {
        const next = [...prev];
        const site = { ...next[siteIndex] };

        // Ensure manufacturingPlantDataList stays an ARRAY, but trim to a single plant
        const currentPlants = Array.isArray(site.manufacturingPlantDataList)
          ? site.manufacturingPlantDataList
          : site.manufacturingPlantDataList
            ? [site.manufacturingPlantDataList]
            : [];

        const firstPlant = currentPlants.length > 0 ? { ...currentPlants[0] } : { ...createEmptyPlant() };
        // Auto-populate mfg plant short name from site short name when site has no multiple plants
        // Only populate if mfg plant short name is not already set
        firstPlant.mfgPlantShortName = firstPlant.mfgPlantShortName || site.siteShortName;
        site.siteHasMultiplePlants = value;
        site.manufacturingPlantDataList = [firstPlant];

        next[siteIndex] = site;
        return next;
      });
    } else if (field === "siteShortName") {
      setSiteRows((prev) => {
        const next = prev.map((row, i) => {
          if (i !== siteIndex) return row;
          const updatedRow = { ...row, [field]: value };
          // If site has no multiple plants, auto-populate mfg plant short name only if not already set
          if (row.siteHasMultiplePlants === "No") {
            const plants = [...(updatedRow.manufacturingPlantDataList || [])];
            if (plants[0] && !plants[0].mfgPlantShortName) {
              plants[0] = { ...plants[0], mfgPlantShortName: value };
            }
            updatedRow.manufacturingPlantDataList = plants;
          }
          return updatedRow;
        });
        return next;
      });
    } else {
      setSiteRows((prev) => {
        const next = prev.map((row, i) =>
          i === siteIndex ? { ...row, [field]: value } : row
        );
        return next;
      });
    }
  };

  const handleSiteFieldBlur = (siteIndex, field) => {
    setSiteRows(prev => {
      const next = [...prev];
      const site = { ...next[siteIndex] };
      const trimmed = String(site[field] || "").trim();
      site[field] = trimmed;
      if (field === "siteShortName" && site.siteHasMultiplePlants === "No") {
        const plants = [...site.manufacturingPlantDataList];
        if (plants[0] && !plants[0].mfgPlantShortName) plants[0] = { ...plants[0], mfgPlantShortName: trimmed };
        site.manufacturingPlantDataList = plants;
      }
      next[siteIndex] = site;
      return next;
    });
  };

  const handlePlantFieldBlur = (siteIndex, plantIndex, field) => {
    setSiteRows(prev => {
      const next = [...prev];
      const site = { ...next[siteIndex] };
      const plants = [...site.manufacturingPlantDataList];
      plants[plantIndex] = { ...plants[plantIndex], [field]: String(plants[plantIndex][field] || "").trim() };
      site.manufacturingPlantDataList = plants;
      next[siteIndex] = site;
      return next;
    });
  };

  const handlePlantFieldChange = (siteIndex, plantIndex, field, value) => {
    setErrors((prev) => {
      let variable = `site_${siteIndex}_plant_${plantIndex}_${field}`
      const { [variable]: Removed, ...rest } = prev;
      return rest;
    })
    setSiteRows((prev) => {
      const next = [...prev];
      const site = { ...next[siteIndex] };
      const plants = [...site.manufacturingPlantDataList];
      plants[plantIndex] = { ...plants[plantIndex], [field]: value };
      site.manufacturingPlantDataList = plants;
      next[siteIndex] = site;
      return next;
    });
  };

  const handleAddNewSite = () => {
    if (stepData.cmoHasMultipleSites !== "Yes") return;
    setSiteRows((prev) => [...prev, createEmptySite()]);
  };

  const handleDeleteSite = (siteIndex) => {
    setErrors(prev => {
      const prefix = `site_${siteIndex}_`;
      const updated = { ...prev };

      Object.keys(updated).forEach(key => {
        if (key.includes(prefix)) {
          delete updated[key];
        }
      });

      return updated;
    });
    if (stepData.cmoHasMultipleSites !== "Yes" || siteRows.length <= 1) return;
    setSiteRows((prev) => prev.filter((_, i) => i !== siteIndex));
  };

  const handleAddManufacturingPlant = (siteIndex) => {
    setSiteRows((prev) => {
      const next = [...prev];
      const site = { ...next[siteIndex] };
      site.manufacturingPlantDataList = [...site.manufacturingPlantDataList, createEmptyPlant()];
      next[siteIndex] = site;
      return next;
    });
  };

  const handleDeleteManufacturingPlant = (siteIndex, plantIndex) => {
    setErrors(prev => {
      const prefix = `site_${siteIndex}_plant_${plantIndex}_`;
      const updated = { ...prev };

      Object.keys(updated).forEach(key => {
        if (key.includes(prefix)) {
          delete updated[key];
        }
      });

      return updated;
    });
    setSiteRows((prev) => {
      const next = [...prev];
      const site = { ...next[siteIndex] };
      const plants = site.manufacturingPlantDataList.filter((_, i) => i !== plantIndex);
      if (plants.length === 0) return prev;
      site.manufacturingPlantDataList = plants;
      next[siteIndex] = site;
      return next;
    });
  };

  const cmoDetailsResponse = JSON.parse(sessionStorage.getItem("cmoDetailsResponse"));
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setStepData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // // Call parent's onFieldChange function if it exists
    // if (typeof onFieldChange === 'function') {
    //   onFieldChange(name, value);
    // }

    // // Update step data if the function exists
    // if (typeof updateStepData === 'function') {
    //   updateStepData({ ...stepData, [name]: value });
    // }
  };
  const validate = () => {
    if (readOnly) return true;

    const newErrors = {};

    if (!stepData.cmoHasMultipleSites) {
      newErrors.cmoHasMultipleSites = "CMO has multiple sites is required";
      setErrors(newErrors);
      return false;
    }

    for (let s = 0; s < siteRows.length; s++) {
      const site = siteRows[s];
      if (!site.siteID || !String(site.siteID).trim()) {
        newErrors[`site_${s}_siteID`] = "Site ID is required";
      } else if (!/^[A-Za-z0-9\-_]{1,20}$/.test(site.siteID)) {
        newErrors[`site_${s}_siteID`] = "Site ID must be alphanumeric, hyphens and underscores only, max 20 characters";
      }
      if (!site.siteShortName || !String(site.siteShortName).trim()) {
        newErrors[`site_${s}_siteShortName`] = "Site Short Name is required";
      } else if (!/^[A-Za-zÀ-ÿ0-9\s\-_]{1,30}$/.test(site.siteShortName)) {
        newErrors[`site_${s}_siteShortName`] = "Site Short Name must be alphanumeric, spaces, hyphens and underscores only, max 30 characters";
      }
      if (!site.siteHasMultiplePlants) {
        newErrors[`site_${s}_siteHasMultiplePlants`] = "Site has multiple Manufacturing Plants is required";
      }
      site.manufacturingPlantDataList.forEach((plant, p) => {
        if (!plant.mfgPlantID || !String(plant.mfgPlantID).trim()) {
          newErrors[`site_${s}_plant_${p}_mfgPlantID`] = "Manufacturing Plant ID is required";
        }
        if (!plant.manufacturingPlantIdSameAsASL) {
          newErrors[`site_${s}_plant_${p}_manufacturingPlantIdSameAsASL`] = "Manufacturing Plant ID same as ASL Number is required";
        }
        if (!plant.mfgPlantShortName || !String(plant.mfgPlantShortName).trim()) {
          newErrors[`site_${s}_plant_${p}_mfgPlantShortName`] = "Manufacturing Plant Short Name is required";
        }
      });
    }

    // Uniqueness: no siteID, siteShortName, mfgPlantID or mfgPlantShortName should be duplicated
    const siteIDs = siteRows.map((site, idx) => ({ value: String((site.siteID || '').trim()).toLowerCase(), siteIndex: idx }));
    const siteShortNames = siteRows.map((site, idx) => ({ value: String((site.siteShortName || '').trim()).toLowerCase(), siteIndex: idx }));
    const mfgPlantIDs = [];
    const mfgPlantShortNames = [];
    siteRows.forEach((site, s) => {
      (site.manufacturingPlantDataList || []).forEach((plant, p) => {
        mfgPlantIDs.push({ value: String((plant.mfgPlantID || '').trim()).toLowerCase(), siteIndex: s, plantIndex: p });
        mfgPlantShortNames.push({ value: String((plant.mfgPlantShortName || '').trim()).toLowerCase(), siteIndex: s, plantIndex: p });
      });
    });

    const findDuplicateKeys = (items) => {
      const byValue = {};
      items.forEach((item) => {
        if (!item.value) return;
        if (!byValue[item.value]) byValue[item.value] = [];
        byValue[item.value].push(item);
      });
      return Object.keys(byValue).filter((v) => byValue[v].length > 1).flatMap((v) => byValue[v]);
    };

    findDuplicateKeys(siteIDs).forEach(({ siteIndex }) => {
      newErrors[`site_${siteIndex}_siteID`] = "Site ID must be unique across all sites";
    });
    findDuplicateKeys(siteShortNames).forEach(({ siteIndex }) => {
      newErrors[`site_${siteIndex}_siteShortName`] = "Site Short Name must be unique across all sites";
    });
    findDuplicateKeys(mfgPlantIDs).forEach(({ siteIndex, plantIndex }) => {
      newErrors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantID`] = "Manufacturing Plant ID must be unique across all plants";
    });
    findDuplicateKeys(mfgPlantShortNames).forEach(({ siteIndex, plantIndex }) => {
      newErrors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantShortName`] = "Manufacturing Plant Short Name must be unique across all plants";
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const token = localStorage.getItem('authToken');
  useEffect(() => {

    if (token) {
      try {
        const base64Url = token.split('.')[1]; // Get payload
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
            .join('')
        );

        const payload = JSON.parse(jsonPayload);
        setBrandOwnerMosymphonyID(payload.organization_id)

      } catch (e) {
        console.error('Invalid token', e);
      }
    }
  }, [token]);


  const handleNextClick = async () => {
    // In readonly mode, just proceed to next step without validation
    if (readOnly) {
      handleNext();
      return;
    }


    setSubmitted(true);

    if (!validate()) {
      return;
    }


    setLoading(true);
    try {
      const vendorMosymphonyID = cmoDetailsResponse?.organizationId;
      const cmoHasMultipleSitesBool = stepData.cmoHasMultipleSites === "Yes";


      const cmoSitesDetails = siteRows.map((site) => ({
        siteDetails: {
          collabrixCMOSiteID: site?.collabrixCMOSiteID,
          siteID: site.siteID,
          siteShortName: site.siteShortName,
        },
        siteManufacturingPlants: (site.manufacturingPlantDataList || []).map((plant) => ({
          collabrixCMOSiteID: plant?.collabrixCMOSiteID,
          collabrixMfgPlantID: plant?.collabrixMfgPlantID,
          mfgPlantID: plant.mfgPlantID,
          mfgPlantShortName: plant.mfgPlantShortName,
          aslNumber: plant.manufacturingPlantIdSameAsASL === "Yes" ? plant.mfgPlantID : "",
        })),
      }));

      if (siteDetailsAddedOnce) {
        let payload = {
          vendorMosymphonyID,
          brandOwnerMosymphonyID: brandOwnerMosymphonyID,
          updateCMOSiteDetailsFlag: true,
          updatePlantDetailsFlag: true,
          status: cmoStepDetails?.status,
          vendorPlantDetails: {
            vendorMosymphonyID,
            cmoHasMultipleSites: cmoHasMultipleSitesBool,
            status: cmoStepDetails?.status,
          },
          cmoSites: cmoSitesDetails.map((val) => ({ siteDetails: val.siteDetails, manufacturingPlantDataList: val.siteManufacturingPlants }))
        };

        if (originalData?.cmoSites && Array.isArray(originalData.cmoSites) && originalData.cmoSites.length > 0) {

          const result = mergeCMOSites(payload.cmoSites, originalData.cmoSites);
          const plantMap = new Map();

          originalData?.cmoSites?.forEach((site) => {
            site?.manufacturingPlantDataList?.forEach((mfg) => {
              const key = `${mfg?.mfgPlantDetails?.collabrixCMOSiteID}_${mfg?.mfgPlantDetails?.collabrixMfgPlantID}`;
              plantMap.set(key, mfg);
            });
          });

          const extractIDsOnly = (list, idField) => {
            if (!Array.isArray(list)) return [];
            return list.map(item => typeof item === 'object' && item !== null ? Number(item[idField]) : Number(item)).filter(id => !isNaN(id));
          };

          payload.cmoSites = result.map((site) => ({
            ...site,
            manufacturingPlantDataList: site.manufacturingPlantDataList.map((plant) => {

              const key = `${plant?.collabrixCMOSiteID}_${plant?.collabrixMfgPlantID}`;
              const matchedPlant = plantMap.get(key);

              if (matchedPlant) {
                return {
                  ...matchedPlant,
                  supplyTypeIDList: extractIDsOnly(matchedPlant?.supplyTypeIDList, 'supplyTypeID'),
                  pickupAddress: matchedPlant?.pickupAddress || {},
                  deliveryAddress: matchedPlant?.deliveryAddress || {},
                  lineDetailsList: matchedPlant?.lineDetailsList || [],
                  plantTypeIDList: extractIDsOnly(matchedPlant?.plantTypeIDList, 'plantTypeID'),
                  mainAddress: matchedPlant?.mainAddress || {},
                  drugTypeIDList: extractIDsOnly(matchedPlant?.drugTypeIDList, 'drugTypeID'),
                  facilityTypeIDList: extractIDsOnly(matchedPlant?.facilityTypeIDList, 'facilityTypeID'),
                  mfgPlantDetails: { ...plant },
                };
              }

              return {
                supplyTypeIDList: [],
                pickupAddress: {},
                deliveryAddress: {},
                lineDetailsList: [],
                plantTypeIDList: [],
                mainAddress: {},
                drugTypeIDList: [],
                facilityTypeIDList: [],
                mfgPlantDetails: { ...plant },
              };
            }),
          }));
          // payload.cmoSites = result.map((val) => {
          //   return {
          //     ...val, manufacturingPlantDataList: val.manufacturingPlantDataList.map((val1) => {
          //       let mfgData = {}
          //       if (val1?.collabrixMfgPlantID && val1?.collabrixCMOSiteID) {
          //         originalData.cmoSites.forEach(element => {
          //           element.manufacturingPlantDataList.forEach(mfg => {
          //             if (mfg?.mfgPlantDetails?.collabrixMfgPlantID &&
          //               mfg?.mfgPlantDetails?.collabrixCMOSiteID &&
          //               mfg?.mfgPlantDetails?.collabrixMfgPlantID === val1?.collabrixMfgPlantID &&
          //               mfg?.mfgPlantDetails?.collabrixCMOSiteID === val1?.collabrixCMOSiteID) {
          //               mfgData = { ...mfg }
          //             } else {
          //               mfgData = {
          //                 supplyTypeIDList: [],
          //                 pickupAddress: {},
          //                 deliveryAddress: {},
          //                 lineDetailsList: [],
          //                 plantTypeIDList: [],
          //                 mainAddress: {},
          //                 drugTypeIDList: [],
          //                 facilityTypeIDList: [],
          //               }
          //             }
          //           });
          //         });
          //       }
          //       return {
          //         ...mfgData,
          //         mfgPlantDetails: { ...val1 },
          //       }
          //     })
          //   }
          // })
          // payload.cmoSites = result.map((val) => ({
          //   ...val, manufacturingPlantDataList: val.manufacturingPlantDataList.map((val1) => ({
          //     mfgPlantDetails: { ...val1 }, 
          //     supplyTypeIDList: [], 
          //     pickupAddress: {},
          //     deliveryAddress: {},
          //     lineDetailsList: [],
          //     plantTypeIDList: [],
          //     mainAddress: {},
          //     drugTypeIDList: [],
          //     facilityTypeIDList: [],
          //   }))
          // }));
        } else {
          payload.cmoSites = []
        }

        if (siteDetailsChanged) {
          const response = await axios.post(`${API_BASE_URL}/updateVendorPlantDetails`, payload);
          if (response.data && response.data.status) {
            // const extractIDsOnly = (list, idField) => {
            //   if (!Array.isArray(list)) return [];
            //   return list.map(item => typeof item === 'object' && item !== null ? Number(item[idField]) : Number(item)).filter(id => !isNaN(id));
            // };

            const plantMap = new Map();
            originalData?.cmoSites?.forEach((site) => {
              site?.manufacturingPlantDataList?.forEach((mfg) => {
                const key = `${mfg?.mfgPlantDetails?.collabrixCMOSiteID}_${mfg?.mfgPlantDetails?.collabrixMfgPlantID}`;
                plantMap.set(key, mfg);
              });
            });

            let updatedCMOSites = response.data.data?.cmoSites;
            updateStepData({
              ...stepData, cmoSites: updatedCMOSites && Array.isArray(updatedCMOSites) ? updatedCMOSites.filter((val) => !val.siteDetails.markForDelete).map((val) => { return { ...val, manufacturingPlantDataList: val.manufacturingPlantDataList.filter((val) => !val.mfgPlantDetails.markForDelete) } }) : [], cmoHasMultipleSites: stepData.cmoHasMultipleSites
            });

            // updateStepData({
            //   ...stepData, cmoSites: siteRows.map((val) => ({
            //     siteDetails: {
            //       collabrixCMOSiteID: val?.collabrixCMOSiteID,
            //       siteID: val.siteID,
            //       siteShortName: val.siteShortName,
            //     },
            //     manufacturingPlantDataList: val.manufacturingPlantDataList.map((plant) => {
            //       const key = `${plant?.collabrixCMOSiteID}_${plant?.collabrixMfgPlantID}`;
            //       const matchedPlant = plantMap.get(key);

            //       if (matchedPlant) {
            //         return {
            //           ...matchedPlant,
            //           supplyTypeIDList: extractIDsOnly(matchedPlant?.supplyTypeIDList, 'supplyTypeID'),
            //           pickupAddress: matchedPlant?.pickupAddress || {},
            //           deliveryAddress: matchedPlant?.deliveryAddress || {},
            //           lineDetailsList: matchedPlant?.lineDetailsList || [],
            //           plantTypeIDList: extractIDsOnly(matchedPlant?.plantTypeIDList, 'plantTypeID'),
            //           mainAddress: matchedPlant?.mainAddress || {},
            //           drugTypeIDList: extractIDsOnly(matchedPlant?.drugTypeIDList, 'drugTypeID'),
            //           facilityTypeIDList: extractIDsOnly(matchedPlant?.facilityTypeIDList, 'facilityTypeID'),
            //           mfgPlantDetails: { ...plant },
            //         };
            //       }

            //       return {
            //         supplyTypeIDList: [],
            //         pickupAddress: {},
            //         deliveryAddress: {},
            //         lineDetailsList: [],
            //         plantTypeIDList: [],
            //         mainAddress: {},
            //         drugTypeIDList: [],
            //         facilityTypeIDList: [],
            //         mfgPlantDetails: { ...plant },
            //       };
            //     }
            //       // ({ mfgPlantDetails: { ...val2 } })
            //     )
            //   })), cmoHasMultipleSites: stepData.cmoHasMultipleSites
            // });
            handleNext();
          } else {
            // updateStepData({ ...stepData, cmoSites: siteRows, cmoHasMultipleSites: stepData.cmoHasMultipleSites });
            // handleNext();
            console.error("Update API call failed:", response.data);
          }
        } else {
          handleNext();
        }



      } else {
        let payload = {
          vendorMosymphonyID,
          brandOwnerMosymphonyID: brandOwnerMosymphonyID,
          // status: cmoStepDetails?.status,
          // cmoHasMultipleSites: cmoHasMultipleSitesBool,
          cmoSites: cmoSitesDetails,
          vendorPlantDetails: {
            status: cmoStepDetails?.status,
            cmoHasMultipleSites: cmoHasMultipleSitesBool,
          }

        };
        const response = await axios.post(`${API_BASE_URL}/addVendorPlantDetails`, payload);
        if (response.data && response.data.status) {
          sessionStorage.setItem("siteDetailsAddedOnce", "true");
          const cmoSitesData = response.data?.data?.cmoSites || cmoSitesDetails

          const extractIDsOnly = (list, idField) => {
            if (!Array.isArray(list)) return [];
            return list.map(item => typeof item === 'object' && item !== null ? Number(item[idField]) : Number(item)).filter(id => !isNaN(id));
          };

          updateStepData({
            ...stepData, cmoSites: cmoSitesData.map((val) => ({
              siteDetails: {
                ...val?.siteDetails
              },
              manufacturingPlantDataList: val.siteManufacturingPlants.map((val2) => ({
                mfgPlantDetails: { ...val2, collabrixCMOSiteID: val?.siteDetails?.collabrixCMOSiteID },
                supplyTypeIDList: extractIDsOnly(val2?.supplyTypeIDList, 'supplyTypeID'),
                pickupAddress: val2?.pickupAddress || {},
                deliveryAddress: val2?.deliveryAddress || {},
                lineDetailsList: val2?.lineDetailsList || [],
                plantTypeIDList: extractIDsOnly(val2?.plantTypeIDList, 'plantTypeID'),
                mainAddress: val2?.mainAddress || {},
                drugTypeIDList: extractIDsOnly(val2?.drugTypeIDList, 'drugTypeID'),
                facilityTypeIDList: extractIDsOnly(val2?.facilityTypeIDList, 'facilityTypeID'),
              }))
            })),
            cmoHasMultipleSites: stepData.cmoHasMultipleSites
          });
          handleNext();
        } else {
          // updateStepData({ ...stepData, cmoSites: siteRows, cmoHasMultipleSites: stepData.cmoHasMultipleSites });
          // handleNext();
          console.error("API call failed:", response.data);
        }


      }
    } catch (err) {
      console.error("Failed to save site details.", err);
      setError("Failed to save site details. Please try again.");
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="cmo-stepper-wrapper site-details-wrapper">
      <div className="stepper-container">
        <form>          <Typography variant="h6" className={classes.formTitle}>
          Site Details
        </Typography>
          {error && <Typography color="error" style={{ marginBottom: 16 }}>{error}</Typography>}
          {/* {submitted && Object.keys(errors).length > 0 && (
            <Typography color="error" style={{ marginBottom: 16 }}>
              Please correct the errors below before proceeding.
            </Typography>
          )} */}
          <Grid container spacing={3}>

            <Grid item xs={12} sm={6}>
              <FormControl className={classes.formField}>
                <Typography component="label" htmlFor="siteID">
                  CMO has multiple sites {!readOnly && <span style={{ color: "red" }}>*</span>}
                </Typography>
                {readOnly ? (
                  <Typography>
                    {typeof formData?.cmoHasMultipleSites === "boolean" ? formData?.cmoHasMultipleSites === true ? "Yes" : "No" : ""}
                  </Typography>
                ) : (
                  <Select
                    select
                    id="cmoHasMultipleSites"
                    name="cmoHasMultipleSites"
                    value={stepData.cmoHasMultipleSites || ""}
                    onChange={handleFieldChange}
                    variant="outlined"
                    placeholder="Select option"
                    fullWidth
                    required
                    InputProps={{ style: commonInlineStyles.input }}
                    sx={inputFieldStyles}
                    inputProps={commonInlineStyles.inputProps}
                    style={commonInlineStyles.input}
                    error={
                      submitted && Boolean(errors.cmoHasMultipleSites)
                    }
                    helperText={
                      submitted && errors.cmoHasMultipleSites
                        ? errors.cmoHasMultipleSites
                        : ""
                    }
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select from the list
                    </MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12}>
              <Typography component="label" htmlFor="siteID">
                Site and Manufacturing Plant Details
              </Typography>
              <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16, opacity: !stepData.cmoHasMultipleSites ? 0.6 : 1, pointerEvents: !stepData.cmoHasMultipleSites ? 'none' : 'auto' }}>
                <Table size="small" stickyHeader style={{ tableLayout: 'auto', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeader}>Site ID&nbsp;{!readOnly && <span style={{ color: "red" }}>*</span>}</TableCell>
                      <TableCell className={classes.tableHeader}>Site Short Name&nbsp;{!readOnly && <span style={{ color: "red" }}>*</span>}</TableCell>
                      <TableCell className={classes.tableHeader}>Site has multiple Manufacturing Plants&nbsp;{!readOnly && <span style={{ color: "red" }}>*</span>}</TableCell>
                      <TableCell className={classes.tableHeader}>Manufacturing Plant ID&nbsp;{!readOnly && <span style={{ color: "red" }}>*</span>}</TableCell>
                      <TableCell className={classes.tableHeader}>Manufacturing Plant ID same as ASL Number&nbsp;{!readOnly && <span style={{ color: "red" }}>*</span>}</TableCell>
                      <TableCell className={classes.tableHeader}>Manufacturing Plant Short Name&nbsp;{!readOnly && <span style={{ color: "red" }}>*</span>}</TableCell>
                      {(stepData.cmoHasMultipleSites === "Yes" && !readOnly) && (
                        <TableCell className={classes.tableHeader} align="center">Action</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {siteRows.map((site, siteIndex) => (
                      <React.Fragment key={siteIndex}>
                        {site.manufacturingPlantDataList.map((plant, plantIndex) => (
                          <TableRow key={`${siteIndex}-${plantIndex}`}>
                            {plantIndex === 0 ? (
                              <>
                                <TableCell className={classes.tableCell} rowSpan={site.manufacturingPlantDataList.length}>
                                  {readOnly ? (
                                    <Typography>{site.siteID || ""}</Typography>
                                  ) : (
                                    <TextField
                                      fullWidth
                                      size="small"
                                      variant="outlined"
                                      placeholder="Enter Value Here"
                                      value={site.siteID || ""}
                                      onChange={(e) => handleSiteFieldChange(siteIndex, "siteID", e.target.value)}
                                      onBlur={() => handleSiteFieldBlur(siteIndex, "siteID")}
                                      disabled={!stepData.cmoHasMultipleSites}
                                      inputProps={{ maxLength: 20, ...commonInlineStyles.inputProps }}
                                      onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^a-zA-Z0-9\-_]/g, "").replace(/(-{2,}|_{2,})/g, (m) => m[0]);
                                      }}
                                      error={
                                        submitted && Boolean(errors[`site_${siteIndex}_siteID`])
                                      }
                                      helperText={
                                        submitted && errors[`site_${siteIndex}_siteID`]
                                          ? errors[`site_${siteIndex}_siteID`]
                                          : ""
                                      }
                                    />
                                  )}
                                </TableCell>
                                <TableCell className={classes.tableCell} rowSpan={site.manufacturingPlantDataList.length}>
                                  {readOnly ? (
                                    <Typography>{site.siteShortName || ""}</Typography>
                                  ) : (
                                    <TextField
                                      fullWidth
                                      size="small"
                                      variant="outlined"
                                      placeholder="Enter Value Here"
                                      value={site.siteShortName || ""}
                                      onChange={(e) => handleSiteFieldChange(siteIndex, "siteShortName", e.target.value)}
                                      onBlur={() => handleSiteFieldBlur(siteIndex, "siteShortName")}
                                      disabled={!stepData.cmoHasMultipleSites}
                                      inputProps={{ maxLength: 30, ...commonInlineStyles.inputProps }}
                                      onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^a-zA-ZÀ-ÿ0-9\s\-_]/g, "").replace(/(-{2,}|_{2,}|\s{2,})/g, (m) => m[0]);
                                      }}
                                      error={
                                        submitted && Boolean(errors[`site_${siteIndex}_siteShortName`])
                                      }
                                      helperText={
                                        submitted && errors[`site_${siteIndex}_siteShortName`]
                                          ? errors[`site_${siteIndex}_siteShortName`]
                                          : ""
                                      }
                                    />
                                  )}
                                </TableCell>
                                <TableCell className={classes.tableCell} rowSpan={site.manufacturingPlantDataList.length}>
                                  {readOnly ? (
                                    <Typography>{site.siteHasMultiplePlants || ""}</Typography>
                                  ) : (
                                    <Select
                                      fullWidth
                                      size="small"
                                      variant="outlined"
                                      value={site.siteHasMultiplePlants || ""}
                                      onChange={(e) => handleSiteFieldChange(siteIndex, "siteHasMultiplePlants", e.target.value)}
                                      disabled={!stepData.cmoHasMultipleSites}
                                      displayEmpty
                                      inputProps={commonInlineStyles.inputProps}
                                      error={
                                        submitted && Boolean(errors[`site_${siteIndex}_siteHasMultiplePlants`])
                                      }
                                      helperText={
                                        submitted && errors[`site_${siteIndex}_siteHasMultiplePlants`]
                                          ? errors[`site_${siteIndex}_siteHasMultiplePlants`]
                                          : ""
                                      }
                                    >
                                      <MenuItem value="" disabled>Select an option</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                      <MenuItem value="No">No</MenuItem>
                                    </Select>
                                  )}
                                </TableCell>
                              </>
                            ) : null}
                            <TableCell className={classes.tableCell}>
                              {readOnly ? (
                                <Typography>{plant.mfgPlantID || ""}</Typography>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder="Enter Value Here"
                                    value={plant.mfgPlantID || ""}
                                    onChange={(e) => handlePlantFieldChange(siteIndex, plantIndex, "mfgPlantID", e.target.value)}
                                    onBlur={() => handlePlantFieldBlur(siteIndex, plantIndex, "mfgPlantID")}
                                    disabled={!stepData.cmoHasMultipleSites}
                                    inputProps={{ ...commonInlineStyles.inputProps }}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, "");
                                      }}
                                    error={
                                      submitted && Boolean(errors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantID`])
                                    }
                                    helperText={
                                      submitted && errors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantID`]
                                        ? errors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantID`]
                                        : ""
                                    }
                                  />
                                  {stepData.cmoHasMultipleSites && site.siteHasMultiplePlants === "Yes" && plantIndex === 0 ? (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAddManufacturingPlant(siteIndex)}
                                      aria-label="Add manufacturing plant"
                                      color="primary"
                                      disabled={!stepData.cmoHasMultipleSites}
                                    >
                                      <AddCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  ) : stepData.cmoHasMultipleSites && site.siteHasMultiplePlants === "Yes" && plantIndex > 0 ? (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteManufacturingPlant(siteIndex, plantIndex)}
                                      aria-label="Delete manufacturing plant"
                                      style={{ color: '#d32f2f' }}
                                      disabled={!stepData.cmoHasMultipleSites}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  ) : null}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {readOnly ? (
                                <Typography>{plant.manufacturingPlantIdSameAsASL || ""}</Typography>
                              ) : (
                                <Select
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  value={plant.manufacturingPlantIdSameAsASL || ""}
                                  onChange={(e) => handlePlantFieldChange(siteIndex, plantIndex, "manufacturingPlantIdSameAsASL", e.target.value)}
                                  disabled={!stepData.cmoHasMultipleSites}
                                  displayEmpty
                                  inputProps={commonInlineStyles.inputProps}
                                  error={
                                    submitted && Boolean(errors[`site_${siteIndex}_plant_${plantIndex}_manufacturingPlantIdSameAsASL`])
                                  }
                                  helperText={
                                    submitted && errors[`site_${siteIndex}_plant_${plantIndex}_manufacturingPlantIdSameAsASL`]
                                      ? errors[`site_${siteIndex}_plant_${plantIndex}_manufacturingPlantIdSameAsASL`]
                                      : ""
                                  }
                                >
                                  <MenuItem value="" disabled>Select an option</MenuItem>
                                  <MenuItem value="Yes">Yes</MenuItem>
                                  <MenuItem disabled value="No">No</MenuItem>
                                </Select>
                              )}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {readOnly ? (
                                <Typography>{plant.mfgPlantShortName || ""}</Typography>
                              ) : (
                                <TextField
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  placeholder="Enter Value Here"
                                  value={plant.mfgPlantShortName || ""}
                                  onChange={(e) => handlePlantFieldChange(siteIndex, plantIndex, "mfgPlantShortName", e.target.value)}
                                  onBlur={() => handlePlantFieldBlur(siteIndex, plantIndex, "mfgPlantShortName")}
                                  disabled={!stepData.cmoHasMultipleSites}
                                  inputProps={{ ...commonInlineStyles.inputProps }}
                                  error={
                                    submitted && Boolean(errors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantShortName`])
                                  }
                                  helperText={
                                    submitted && errors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantShortName`]
                                      ? errors[`site_${siteIndex}_plant_${plantIndex}_mfgPlantShortName`]
                                      : ""
                                  }
                                />
                              )}
                            </TableCell>
                            {(stepData.cmoHasMultipleSites === "Yes" && !readOnly) && (
                              <TableCell className={classes.tableCell} align="center">
                                {plantIndex === 0 && !readOnly ? (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteSite(siteIndex)}
                                    aria-label="Delete site"
                                    style={{ color: '#d32f2f' }}
                                    disabled={!stepData.cmoHasMultipleSites || siteRows.length <= 1}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                ) : null}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {stepData.cmoHasMultipleSites === "Yes" && !readOnly && (
                <Button
                  variant="outlined"
              color="default"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddNewSite}
                  className={classes.addSiteButton}
                >
                  Add New Site
                </Button>
              )}
            </Grid>
          </Grid>
          {!hideStepActions && <div className="stepper-navigation-btns" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
            <Button variant="outlined" startIcon={<ArrowBackIosIcon />} color="default" onClick={handleBack} className={classes.backButton} disabled={loading}>
              Back
            </Button>
            <Button variant="contained" color="primary" onClick={handleNextClick} className={classes.button} endIcon={loading ? <CircularProgress size={20} /> : <ArrowForwardIosIcon style={{ fontSize: 15 }} />} disabled={loading}>
              {loading ? "Saving..." : "Save & Next"}
            </Button>
          </div>}
        </form>
        {readOnly && <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, }} />}

      </div>
    </div>
  );
};

export default SiteDetails;